/**
 * 渐变配置接口（用于 iOS/Android）
 */
export interface GradientConfig {
  gradientType: 'linear' | 'radial';
  colors: number[][]; // [[R, G, B], ...] 每个颜色是 [0-255] 的整数数组
  positions: number[]; // [0~1] 浮点数数组
  direction: number[]; // [startX, startY, endX, endY] 方向向量
  alpha?: number; // 透明度 0~1，默认 1.0
  // 径向渐变额外属性
  center?: number[]; // [x, y] 中心点坐标 0~1
  radius?: number; // 半径 0~1
}

/**
 * 角度转方向向量
 * @param angle 角度（0-360）
 * @returns [startX, startY, endX, endY]
 */
const angleToDirection = (angle: number): number[] => {
  // 将角度转换为弧度
  const radians = ((angle - 90) * Math.PI) / 180;
  // 计算方向向量
  const endX = Math.cos(radians);
  const endY = Math.sin(radians);
  // 线性渐变从中心点开始
  return [0.5, 0.5, 0.5 + endX, 0.5 + endY];
};

/**
 * HEX 转 RGB 数组
 */
const hexToRgbArray = (hex: string): number[] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return [0, 0, 0];
  return [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16),
  ];
};

/**
 * RGB 字符串转 RGB 数组
 */
const rgbStringToArray = (rgbStr: string): number[] => {
  const matches = rgbStr.match(/\d+/g);
  if (!matches || matches.length < 3) return [0, 0, 0];
  return matches.slice(0, 3).map(Number);
};

/**
 * 解析 CSS 渐变字符串为移动端友好的结构化参数
 * 支持格式：
 * - linear-gradient(90deg, #108ee9 0%, #87d068 100%)
 * - linear-gradient(to right, #108ee9 0%, #87d068 100%)
 * - radial-gradient(circle, #108ee9 0%, #87d068 100%)
 */
export const parseGradientCssToMobile = (
  cssStr: string | undefined | null
): GradientConfig | null => {
  if (!cssStr || !cssStr.includes('gradient')) {
    return null;
  }

  const result: GradientConfig = {
    gradientType: 'linear',
    colors: [],
    positions: [],
    direction: [0, 0, 1, 0], // 默认左到右
    alpha: 1.0,
  };

  // 1. 解析渐变类型
  if (cssStr.startsWith('radial-gradient')) {
    result.gradientType = 'radial';
    result.center = [0.5, 0.5];
    result.radius = 0.5;
  }

  // 2. 解析方向（线性渐变）
  if (result.gradientType === 'linear') {
    // 匹配角度格式：linear-gradient(90deg, ...)
    const angleMatch = cssStr.match(/linear-gradient\((\d+)deg/);
    if (angleMatch) {
      const angle = parseInt(angleMatch[1], 10);
      result.direction = angleToDirection(angle);
    } else {
      // 匹配方向关键词：linear-gradient(to right, ...)
      const directionMatch = cssStr.match(/to\s+(\w+)(-(\w+))?/);
      if (directionMatch) {
        const [, dir1, , dir2] = directionMatch;
        const direction = `${dir1}${dir2 ? '-' + dir2 : ''}`;
        switch (direction) {
          case 'right':
            result.direction = [0, 0, 1, 0]; // 左→右
            break;
          case 'left':
            result.direction = [1, 0, 0, 0]; // 右→左
            break;
          case 'bottom':
            result.direction = [0, 0, 0, 1]; // 上→下
            break;
          case 'top':
            result.direction = [0, 1, 0, 0]; // 下→上
            break;
          case 'right-bottom':
          case 'bottom-right':
            result.direction = [0, 0, 1, 1]; // 左上→右下
            break;
          case 'left-bottom':
          case 'bottom-left':
            result.direction = [1, 0, 0, 1]; // 右上→左下
            break;
          case 'right-top':
          case 'top-right':
            result.direction = [0, 1, 1, 0]; // 左下→右上
            break;
          case 'left-top':
          case 'top-left':
            result.direction = [1, 1, 0, 0]; // 右下→左上
            break;
          default:
            result.direction = [0, 0, 1, 0]; // 默认左→右
        }
      }
    }
  }

  // 3. 解析颜色节点和位置
  // 匹配 HEX 或 RGB 格式 + 百分比
  const colorStopReg =
    /(#[0-9a-fA-F]{6}|rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\))\s*(\d+(?:\.\d+)?)%/g;
  const matches = Array.from(cssStr.matchAll(colorStopReg));

  for (const match of matches) {
    const [, color, position] = match;
    const pos = parseFloat(position) / 100; // 转为 0~1 浮点数
    result.positions.push(pos);

    // 转换颜色为 [R, G, B] 整数数组（0-255）
    if (color.startsWith('#')) {
      // HEX 转 RGB 数组
      result.colors.push(hexToRgbArray(color));
    } else if (color.startsWith('rgb')) {
      // RGB 字符串转数组
      result.colors.push(rgbStringToArray(color));
    }
  }

  // 验证数据有效性
  if (result.colors.length < 2 || result.positions.length < 2) {
    return null;
  }

  // 确保 colors 和 positions 数量一致
  if (result.colors.length !== result.positions.length) {
    return null;
  }

  return result;
};

/**
 * 将渐变配置转换为 JSON 字符串（用于 API 提交）
 */
export const gradientConfigToJson = (config: GradientConfig | null): string => {
  if (!config) return '';
  return JSON.stringify(config);
};

/**
 * 从 JSON 字符串解析渐变配置
 */
export const gradientConfigFromJson = (jsonStr: string): GradientConfig | null => {
  if (!jsonStr) return null;
  try {
    return JSON.parse(jsonStr) as GradientConfig;
  } catch {
    return null;
  }
};

/**
 * 将渐变配置转换回 CSS 字符串（用于回显）
 */
export const gradientConfigToCss = (config: GradientConfig | null): string => {
  if (!config || config.colors.length < 2) {
    return '';
  }

  // 将 RGB 数组转换为 HEX
  const rgbToHex = (r: number, g: number, b: number): string => {
    return `#${[r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? `0${hex}` : hex;
      })
      .join('')}`;
  };

  const colorStops = config.colors
    .map((color, index) => {
      const hex = rgbToHex(color[0], color[1], color[2]);
      const percent = (config.positions[index] * 100).toFixed(1);
      return `${hex} ${percent}%`;
    })
    .join(', ');

  if (config.gradientType === 'radial') {
    return `radial-gradient(circle, ${colorStops})`;
  }

  // 线性渐变：从方向向量计算角度
  const [startX, startY, endX, endY] = config.direction;
  const dx = endX - startX;
  const dy = endY - startY;
  const angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;

  return `linear-gradient(${angle.toFixed(0)}deg, ${colorStops})`;
};
